import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import activoClassificationService from '../services/activoClassificationService';

const ActivoClassificationContext = createContext(null);

export function ActivoClassificationProvider({ children }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await activoClassificationService.getFormData({ active_only: true });
      if (res.success && res.data && Array.isArray(res.data.hierarchy)) {
        setHierarchy(res.data.hierarchy);
        setAreas(Array.isArray(res.data.areas) ? res.data.areas : []);
      } else {
        setHierarchy([]);
        setAreas([]);
        setError(res.message || 'No se pudo cargar la clasificación de activos');
      }
    } catch (e) {
      setHierarchy([]);
      setAreas([]);
      setError(e.response?.data?.message || e.message || 'Error al cargar la clasificación de activos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(
    () =>
      hierarchy.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.name,
        description: c.description,
        is_active: c.is_active,
      })),
    [hierarchy]
  );

  const subcategories = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).map((s) => ({
          ...s,
          category: { id: c.id, code: c.code, name: c.name },
          category_id: s.category_id ?? c.id,
          activo_category_id: s.activo_category_id ?? c.id,
        }))
      ),
    [hierarchy]
  );

  const families = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).flatMap((s) =>
          (s.families || []).map((f) => ({
            ...f,
            subcategory: {
              id: s.id,
              code: s.code,
              name: s.name,
              activo_subcategory_id: s.id,
              category_id: c.id,
              activo_category_id: c.id,
            },
          }))
        )
      ),
    [hierarchy]
  );

  const value = useMemo(
    () => ({
      hierarchy,
      areas,
      loading,
      error,
      refetch: load,
      categories,
      subcategories,
      families,
    }),
    [hierarchy, areas, loading, error, load, categories, subcategories, families]
  );

  return (
    <ActivoClassificationContext.Provider value={value}>
      {children}
    </ActivoClassificationContext.Provider>
  );
}

export function useActivoClassification() {
  const ctx = useContext(ActivoClassificationContext);
  if (!ctx) {
    throw new Error('useActivoClassification debe usarse dentro de ActivoClassificationProvider');
  }
  return ctx;
}

export default ActivoClassificationContext;
